import React, { useEffect, useMemo, useState } from 'react';
import { PlayIcon } from '@heroicons/react/24/outline';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { calendarDate, dateParam } from '../../accounting/reports/reportUtils';
import type { SafetyReportParam } from '../../../types/safetySystem';

/**
 * Parameter inputs for one Safety Report Center report, generated from the catalog's
 * `params[]`. Values are held in the API's string form (`YYYY-MM-DD` for dates,
 * `YYYY-MM-DDTHH:MM` local for datetimes, option value for selects, raw text / textarea) so
 * `onRun(values)` can go straight to the service and be reused for the xlsx download.
 */

export type ReportParamValues = Record<string, string>;

/** Local wall-clock `YYYY-MM-DDTHH:MM` (what the API's datetime params expect). */
export function dateTimeParam(date: Date): string {
  return `${dateParam(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Parse the API string back into a local Date for the picker; null when blank/invalid. */
export function parseParamDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function defaultValues(params: SafetyReportParam[]): ReportParamValues {
  const out: ReportParamValues = {};
  for (const p of params) out[p.name] = p.default ?? '';
  return out;
}

/** The values the API should see: blanks dropped so the server applies its defaults. */
export function compactValues(values: ReportParamValues): ReportParamValues {
  const out: ReportParamValues = {};
  for (const [k, v] of Object.entries(values)) if (v.trim() !== '') out[k] = v.trim();
  return out;
}

interface Props {
  params: SafetyReportParam[];
  onRun: (values: ReportParamValues) => void;
  running?: boolean;
  disabled?: boolean;
}

const FIELD = 'h-9 w-full rounded-md border border-rule-strong bg-field px-2 text-sm';

export default function ReportParamsForm({ params, onRun, running = false, disabled = false }: Props) {
  const [values, setValues] = useState<ReportParamValues>(() => defaultValues(params));
  const [error, setError] = useState('');

  // A report switch brings a new params list: start again from its defaults.
  const paramsKey = useMemo(() => JSON.stringify(params.map((p) => [p.name, p.default])), [params]);
  useEffect(() => { setValues(defaultValues(params)); setError(''); }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const missing = params.filter((p) => p.required && !(values[p.name] ?? '').trim());
    if (missing.length) {
      setError(`${missing.map((p) => p.label).join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`);
      return;
    }
    setError('');
    onRun(compactValues(values));
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {params.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {params.map((p) => {
            const label = p.required ? `${p.label} *` : p.label;
            const value = values[p.name] ?? '';
            if (p.kind === 'date' || p.kind === 'datetime') {
              return (
                <DateTimePicker
                  key={p.name}
                  mode={p.kind}
                  labelType="custom"
                  label={label}
                  ariaLabel={p.label}
                  value={parseParamDate(value)}
                  disabled={disabled}
                  onChange={(v) => {
                    const d = calendarDate(v);
                    set(p.name, d ? (p.kind === 'date' ? dateParam(d) : dateTimeParam(d)) : '');
                  }}
                />
              );
            }
            if (p.kind === 'select') {
              return (
                <label key={p.name} className="flex flex-col gap-1 text-sm">
                  <span className="text-label">{label}</span>
                  <SingleSelect
                    ariaLabel={p.label}
                    value={value}
                    onChange={(v) => set(p.name, v)}
                    options={p.options ?? []}
                    disabled={disabled}
                    size="sm"
                    usePortal
                  />
                </label>
              );
            }
            if (p.kind === 'textarea') {
              return (
                <label key={p.name} className="flex flex-col gap-1 text-sm sm:col-span-2 lg:col-span-3">
                  <span className="text-label">{label}</span>
                  <textarea
                    aria-label={p.label}
                    value={value}
                    disabled={disabled}
                    rows={4}
                    spellCheck={false}
                    onChange={(e) => set(p.name, e.target.value)}
                    className="w-full rounded-md border border-rule-strong bg-field px-2 py-1.5 text-sm"
                  />
                </label>
              );
            }
            return (
              <label key={p.name} className="flex flex-col gap-1 text-sm">
                <span className="text-label">{label}</span>
                <input
                  type="text"
                  aria-label={p.label}
                  value={value}
                  disabled={disabled}
                  onChange={(e) => set(p.name, e.target.value)}
                  className={FIELD}
                />
              </label>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={disabled || running}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlayIcon width={16} aria-hidden="true" />
          {running ? 'Running…' : 'Run'}
        </button>
        {error && <p role="alert" className="text-sm text-danger-fg">{error}</p>}
      </div>
    </form>
  );
}
